import { SlideoutMenu } from "@components";
import { Button } from "@components/button/button-untitledui";
import { dayjs } from "@lib";
import { Clock, Download01, File02, User01, UserX02 } from "@untitledui/icons";
import { useDownloadInfractionAttachment } from "../hooks/use-download-infraction-attachment";
import { useInfractionDetail } from "../query/infraction-detail.query";
import type { InfractionDetail } from "../types/infraction.types";
import { formatCurrencyBrl, formatFileSize } from "../utils/formatters";
import { InfractionHistoryTimeline } from "./infraction-history-timeline";
import { InfractionStatusBadge } from "./infraction-status-badge";
import { InfractionStatusBannerCard } from "./infraction-status-banner-card";

type InfractionDetailDrawerProps = {
	isOpen: boolean;
	infractionId: string | null;
	onOpenChange: (open: boolean) => void;
	onEdit?: (id: string) => void;
};

function DetailField({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex flex-col gap-1">
			<p className="font-semibold text-fg-secondary text-xs">{label}</p>
			<p className="font-bold text-fg-primary text-sm">{value}</p>
		</div>
	);
}

function InfractionDetailView({
	detail,
	isDownloading,
	onDownloadAttachment,
}: {
	detail: InfractionDetail;
	isDownloading: boolean;
	onDownloadAttachment: (attachmentId: string) => void;
}) {
	return (
		<div className="flex flex-col gap-6 pb-6">
			{detail.deadlineAlertTitle ? (
				<div
					className="flex gap-4 rounded-lg border border-border-error bg-bg-error-primary p-4"
					role="alert"
				>
					<div className="flex size-9 shrink-0 items-center justify-center rounded bg-fg-error-primary">
						<Clock className="size-5 text-fg-white" />
					</div>
					<div className="flex flex-col gap-0.5">
						<p className="font-bold text-error-primary text-sm">
							{detail.deadlineAlertTitle}
						</p>
						{detail.deadlineAlertSubtitle ? (
							<p className="text-error-primary text-xs opacity-90">
								{detail.deadlineAlertSubtitle}
							</p>
						) : null}
					</div>
				</div>
			) : null}

			<section className="flex flex-col gap-4">
				<h3 className="font-bold text-fg-primary text-md leading-6">
					Status da Infração
				</h3>
				<InfractionStatusBannerCard
					displayStatus={detail.displayStatus}
					statusCode={detail.statusCode}
					statusLabel={detail.statusLabel}
				/>
			</section>

			<section className="flex flex-col gap-4">
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<div className="flex items-center justify-between gap-2">
						<div>
							<p className="font-bold text-fg-primary text-sm">
								{detail.plate}
							</p>
							<p className="font-medium text-fg-secondary text-sm">
								{detail.vehicleLabel}
							</p>
						</div>
						<InfractionStatusBadge
							displayStatus={detail.displayStatus}
							label={detail.statusLabel}
						/>
					</div>
					{detail.isAntt ? (
						<div className="flex justify-end">
							<span className="h-fit rounded-md bg-[#E7E8E9] px-2 py-0.5 font-bold text-fg-primary text-xs">
								Infração ANTT
							</span>
						</div>
					) : null}
				</div>

				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<DetailField label="Código" value={detail.infractionTypeCode} />
					<DetailField label="Órgão" value={detail.issuerName} />
					<DetailField
						label="Data"
						value={`${dayjs(detail.occurredAt).format("DD/MM/YYYY")} ${detail.occurredTime}`}
					/>
					<DetailField label="Local" value={detail.location} />
					<DetailField label="Tipo" value={detail.severityTypeLabel} />
					<DetailField label="Valor" value={formatCurrencyBrl(detail.amount)} />
				</div>

				<div className="rounded bg-bg-secondary p-4">
					<p className="mb-1 font-semibold text-fg-secondary text-xs">
						Descrição
					</p>
					<p className="font-medium text-fg-primary text-sm">
						{detail.description}
					</p>
				</div>
			</section>

			<section className="flex flex-col gap-4">
				<h3 className="font-bold text-fg-primary text-sm uppercase tracking-wide">
					Condutor Responsável
				</h3>
				{detail.driverName ? (
					<div className="flex items-center gap-3 rounded-lg border border-border-primary bg-bg-primary px-4 py-3">
						<div className="flex size-10 items-center justify-center rounded-full bg-bg-brand-secondary">
							<User01 className="size-5 text-fg-brand-primary" />
						</div>
						<p className="font-bold text-fg-primary text-sm">
							{detail.driverName}
						</p>
					</div>
				) : (
					<div className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-[#c2c6d7] border-dashed bg-[rgba(243,244,245,0.5)] p-8">
						<UserX02 className="size-6 text-fg-quaternary" />
						<p className="text-center font-medium text-fg-secondary text-sm">
							Nenhum condutor indicado ainda
						</p>
					</div>
				)}
			</section>

			{detail.attachments.length > 0 ? (
				<section className="flex flex-col gap-4">
					<h3 className="font-bold text-fg-primary text-sm uppercase tracking-wide">
						Anexos
					</h3>
					<ul className="flex flex-col gap-2">
						{detail.attachments.map((attachment) => {
							const fileSizeLabel = formatFileSize(attachment.fileSizeBytes);

							return (
								<li
									className="flex items-center justify-between rounded border border-border-secondary bg-bg-primary p-3 shadow-xs"
									key={attachment.id}
								>
									<div className="flex min-w-0 items-center gap-3">
										<File02 className="size-4 shrink-0 text-fg-brand-primary" />
										<div className="min-w-0">
											<span className="block truncate font-medium text-fg-primary text-sm">
												{attachment.fileName}
											</span>
											{attachment.typeLabel || fileSizeLabel ? (
												<span className="text-fg-tertiary text-xs">
													{[attachment.typeLabel, fileSizeLabel]
														.filter(Boolean)
														.join(" · ")}
												</span>
											) : null}
										</div>
									</div>
									<Button
										aria-label={`Baixar ${attachment.fileName}`}
										className="p-1.5"
										color="link-gray"
										isDisabled={isDownloading}
										onClick={() => {
											onDownloadAttachment(attachment.id);
										}}
										size="sm"
										tabIndex={0}
										type="button"
									>
										<Download01 className="size-3.5" />
									</Button>
								</li>
							);
						})}
					</ul>
				</section>
			) : null}

			<InfractionHistoryTimeline history={detail.history} />
		</div>
	);
}

function InfractionDetailFooter({
	canEdit,
	infractionId,
	onEdit,
}: {
	canEdit: boolean;
	infractionId: string;
	onEdit?: (id: string) => void;
}) {
	if (!canEdit) {
		return null;
	}

	return (
		<SlideoutMenu.Footer className="flex gap-2">
			<Button
				aria-label="Editar dados da infração"
				className="flex-1"
				color="secondary"
				onClick={() => {
					onEdit?.(infractionId);
				}}
				size="md"
				tabIndex={0}
				type="button"
			>
				Editar Dados
			</Button>
		</SlideoutMenu.Footer>
	);
}

export function InfractionDetailDrawer({
	isOpen,
	infractionId,
	onOpenChange,
	onEdit,
}: InfractionDetailDrawerProps) {
	const {
		data: detail,
		isLoading,
		isError,
	} = useInfractionDetail(infractionId, isOpen);
	const { mutate: downloadAttachment, isPending: isDownloading } =
		useDownloadInfractionAttachment();

	const close = () => {
		onOpenChange(false);
	};

	if (!isOpen) {
		return null;
	}

	return (
		<SlideoutMenu
			className="w-full max-w-[625px]"
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			style={{ zIndex: 10 }}
		>
			{() => (
				<>
					<SlideoutMenu.Header className="py-4 md:px-4" onClose={close}>
						<h2 className="font-bold text-fg-primary text-xl">
							Detalhes da Infração
						</h2>
					</SlideoutMenu.Header>

					<SlideoutMenu.Content className="md:px-4">
						{isLoading ? (
							<div className="py-12 text-center text-fg-secondary text-sm">
								Carregando detalhes...
							</div>
						) : null}

						{isError ? (
							<div className="py-12 text-center text-error-primary text-sm">
								Não foi possível carregar os detalhes da infração.
							</div>
						) : null}

						{detail ? (
							<InfractionDetailView
								detail={detail}
								isDownloading={isDownloading}
								onDownloadAttachment={(attachmentId) => {
									if (infractionId) {
										downloadAttachment({
											infractionId,
											attachmentId,
										});
									}
								}}
							/>
						) : null}
					</SlideoutMenu.Content>

					{detail ? (
						<InfractionDetailFooter
							canEdit={detail.canEdit}
							infractionId={detail.id}
							onEdit={onEdit}
						/>
					) : null}
				</>
			)}
		</SlideoutMenu>
	);
}
